import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { createMember, submitWaiver, createStripeSession, getStripeCustomerInfo } from './api';
import './index.css';
import RegisterMemberForm from './RegisterMemberForm';

// Add updateMember API helper
async function updateMember(memberId: string, data: any) {
  const res = await fetch(`/api/public/members/${memberId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Failed to update member');
  return res.json();
}

const SignupContext = createContext<any>(null);
const useSignup = () => useContext(SignupContext);

const buttonClass =
  "w-full bg-gold text-navy font-semibold py-3 rounded-lg shadow hover:bg-dark-gold focus:ring-2 focus:ring-gold focus:outline-none transition disabled:opacity-60 disabled:cursor-not-allowed";
const inputClass =
  "w-full px-4 py-2 border border-navy rounded-lg focus:ring-2 focus:ring-gold focus:outline-none text-navy placeholder:text-navy/60 bg-white";

const ProfilePage = ({ email, onCompleteStep, onBack }: { email: string, onCompleteStep: (student: any, step: string) => void, onBack?: () => void }) => {
  const [students, setStudents] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editField, setEditField] = React.useState<{ id: string, field: string } | null>(null);
  const [editValue, setEditValue] = React.useState<string>('');
  const [editLoading, setEditLoading] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);
  const [stripeInfo, setStripeInfo] = React.useState<Record<string, any>>({});

  const fetchStudents = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch('/api/public/members/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Not found');
      const data = await res.json();
      setStudents(data);
    } catch (err: any) {
      setError(err.message || 'No record found');
    } finally {
      setLoading(false);
    }
  }, [email]);

  React.useEffect(() => { fetchStudents(); }, [fetchStudents]);

  React.useEffect(() => {
    if (students.length === 0) return;
    students.forEach(async (student) => {
      if (!student.id) return;
      try {
        const res = await getStripeCustomerInfo(student.id);
        setStripeInfo((prev: any) => ({ ...prev, [student.id]: res.stripe }));
      } catch {
        setStripeInfo((prev: any) => ({ ...prev, [student.id]: null }));
      }
    });
  }, [students]);

  const handleEditClick = (student: any, field: string) => {
    setEditField({ id: student.id, field });
    setEditValue(student[field] || '');
    setEditError(null);
  };
  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };
  const handleEditSave = async (student: any, field: string) => {
    setEditLoading(true); setEditError(null);
    try {
      await updateMember(student.id, { [field]: editValue });
      setEditField(null);
      await fetchStudents();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update');
    } finally {
      setEditLoading(false);
    }
  };
  const handleEditCancel = () => {
    setEditField(null);
    setEditValue('');
  };

  function isInfoComplete(student: any) {
    return student.student_name && student.email && student.contact_phone_number;
  }

  return (
    <div className="max-w-3xl mx-auto mt-12 bg-white rounded-2xl shadow-xl p-8 border border-gold">
      <h2 className="text-3xl font-bold text-navy mb-6">Student Profile(s)</h2>
      {onBack && <button className="mb-4 underline text-navy/70 text-sm" onClick={onBack}>Back</button>}
      {loading ? <div>Loading...</div> : error ? <div className="text-red-600">{error}</div> : (
        <div className="space-y-6">
          {students.map((student) => (
            <div key={student.id} className="border border-navy/10 rounded-lg p-4 bg-navy/5">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Student Name:</span>
                  <span>{student.student_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Pronouns:</span>
                  {editField && editField.id === student.id && editField.field === 'pronouns' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" placeholder="e.g. she/her, he/him, they/them" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'pronouns')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.pronouns || <span className="text-navy/40">(not provided)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'pronouns')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Date of Birth:</span>
                  <span>{student.student_date_of_birth || <span className="text-navy/40">(not provided)</span>}</span>
                  {student.student_date_of_birth && (
                    <span className="ml-2 text-navy/60 text-xs">(Age: {(() => {
                      const dob = new Date(student.student_date_of_birth);
                      const today = new Date();
                      let age = today.getFullYear() - dob.getFullYear();
                      const m = today.getMonth() - dob.getMonth();
                      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                      return age;
                    })()})</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Parent/Guardian:</span>
                  {editField && editField.id === student.id && editField.field === 'parent_guardian_name' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'parent_guardian_name')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.parent_guardian_name || <span className="text-navy/40">(not provided)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'parent_guardian_name')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Address:</span>
                  {editField && editField.id === student.id && editField.field === 'address' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'address')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.address || <span className="text-navy/40">(not provided)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'address')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">City:</span>
                  {editField && editField.id === student.id && editField.field === 'city' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'city')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.city || <span className="text-navy/40">(not provided)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'city')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">State:</span>
                  {editField && editField.id === student.id && editField.field === 'state' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'state')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.state || <span className="text-navy/40">(not provided)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'state')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Zip:</span>
                  {editField && editField.id === student.id && editField.field === 'zip' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'zip')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.zip || <span className="text-navy/40">(not provided)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'zip')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Belt Size:</span>
                  <span>{student.belt_size || <span className="text-navy/40">(not provided)</span>}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Uniform Size:</span>
                  <span>{student.uniform_size || <span className="text-navy/40">(not provided)</span>}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Emergency Contact Name:</span>
                  {editField && editField.id === student.id && editField.field === 'emergency_contact_name' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" placeholder="Full Name" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'emergency_contact_name')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.emergency_contact_name || <span className="text-navy/40">(not provided)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'emergency_contact_name')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Emergency Contact Phone:</span>
                  {editField && editField.id === student.id && editField.field === 'emergency_contact_phone' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" placeholder="Phone Number" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'emergency_contact_phone')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.emergency_contact_phone || <span className="text-navy/40">(not provided)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'emergency_contact_phone')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Email:</span>
                  {editField && editField.id === student.id && editField.field === 'email' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'email')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.email}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'email')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Phone:</span>
                  {editField && editField.id === student.id && editField.field === 'contact_phone_number' ? (
                    <>
                      <input value={editValue} onChange={handleEditChange} className="border rounded px-2 py-1" />
                      <button className="bg-gold text-navy font-semibold px-2 py-1 rounded" onClick={() => handleEditSave(student, 'contact_phone_number')} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                      <button className="bg-gray-200 text-navy px-2 py-1 rounded" onClick={handleEditCancel}>Cancel</button>
                      {editError && <span className="text-red-600 text-sm ml-2">{editError}</span>}
                    </>
                  ) : (
                    <>
                      <span>{student.contact_phone_number || <span className="text-navy/40">(none)</span>}</span>
                      <button className="ml-2 underline text-navy/70 text-xs" onClick={() => handleEditClick(student, 'contact_phone_number')}>Edit</button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-navy w-40">Membership Type:</span>
                  <span>{student.membership_type || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-semibold text-navy w-40">Stripe Payment Info:</span>
                  {stripeInfo[student.id] && stripeInfo[student.id].customer ? (
                    <span>
                      <b>ID:</b> {stripeInfo[student.id].customer.id} <b>Email:</b> {stripeInfo[student.id].customer.email}
                      {stripeInfo[student.id].subscriptions && stripeInfo[student.id].subscriptions.length > 0 && (
                        <>
                          {' '}<b>Subscription:</b> {stripeInfo[student.id].subscriptions[0].status}
                        </>
                      )}
                    </span>
                  ) : (
                    <span className="text-navy/40">No Stripe info</span>
                  )}
                </div>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mt-2">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-navy w-40">Waiver Status:</span>
                      {student.waiver_signed ? (
                        <span className="text-green-700 font-semibold">Signed</span>
                      ) : (
                        <span className="text-red-600 font-semibold">Not Signed</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-navy w-40">Payment Status:</span>
                      {student.payment_complete ? (
                        <span className="text-green-700 font-semibold">Complete</span>
                      ) : (
                        <span className="text-red-600 font-semibold">Incomplete</span>
                      )}
                    </div>
                    {!student.waiver_signed && (
                      <button className={buttonClass} onClick={() => onCompleteStep(student, 'waiver')}>Complete Waiver</button>
                    )}
                    {student.waiver_signed && !student.payment_complete && (
                      <>
                        <button
                          className={buttonClass + (isInfoComplete(student) ? '' : ' opacity-50 cursor-not-allowed')}
                          onClick={() => isInfoComplete(student) && onCompleteStep(student, 'payment')}
                          disabled={!isInfoComplete(student)}
                        >
                          Complete Payment
                        </button>
                        {!isInfoComplete(student) && (
                          <div className="text-red-600 text-xs mt-1">Please fill out all required information (name, email, phone) before continuing to payment.</div>
                        )}
                      </>
                    )}
                    {student.waiver_signed && student.payment_complete && (
                      <span className="text-green-700 font-semibold">Membership Complete</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { setMembershipType, setSignupForm, setMemberId } = useSignup();
  const [showMembershipOptions, setShowMembershipOptions] = useState(false);
  const [showMembershipTypeSelect, setShowMembershipTypeSelect] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState<any>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileEmail, setProfileEmail] = useState<string | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSearchLoading(true); setSearchError(null); setSearchResult(null);
    try {
      const res = await fetch('/api/public/members/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: searchEmail }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Not found');
      const data = await res.json();
      setSearchResult(data);
    } catch (err: any) {
      setSearchError(err.message || 'No record found');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleUseRecord = (student: any) => {
    setProfileEmail(student.email);
    setShowProfile(true);
  };

  if (showProfile && profileEmail) {
    return <ProfilePage email={profileEmail} onCompleteStep={async (student, step) => {
      setMemberId(student.id);
      setSignupForm(student);
      if (step === 'waiver') {
        navigate('/waiver');
      }
      if (step === 'payment') {
        window.location.href = STRIPE_LINKS[student.membership_type] || STRIPE_LINKS['month_to_month'];
      }
    }} onBack={() => { setShowProfile(false); setProfileEmail(null); }} />;
  }

  return (
    <div className="max-w-2xl mx-auto mt-12 bg-white rounded-2xl shadow-xl p-8 border border-gold">
      <h2 className="text-3xl font-bold text-navy mb-6">Summit Membership Signup</h2>
      <div className="mb-6 flex flex-col gap-4">
        {!showMembershipOptions && !showMembershipTypeSelect && (
          <>
            <button className={buttonClass} onClick={() => setShowMembershipOptions(true)}>New Membership / Trial Signup</button>
            <button className={buttonClass + ' bg-gold text-navy font-semibold rounded-xl shadow hover:bg-dark-gold focus:ring-2 focus:ring-gold focus:outline-none transition'} onClick={() => setShowSearch(s => !s)}>
              {showSearch ? 'Hide' : 'Find Existing Record'}
            </button>
          </>
        )}
      </div>
      {showSearch && !showMembershipOptions && !showMembershipTypeSelect && (
        <form onSubmit={handleSearch} className="mb-6 bg-navy/5 p-4 rounded-lg">
          <label className="block mb-2 font-semibold text-navy">Search by Email</label>
          <input type="email" value={searchEmail} onChange={e => setSearchEmail(e.target.value)} className={inputClass} placeholder="Enter your email" required />
          <button type="submit" className={buttonClass + ' mt-4'} disabled={searchLoading}>{searchLoading ? 'Searching...' : 'Search'}</button>
          {searchError && <div className="text-red-600 mt-2 text-sm">{searchError}</div>}
          {Array.isArray(searchResult) && searchResult.length > 0 && (
            <div className="mt-4 bg-white border border-gold rounded-lg p-4">
              <div className="font-semibold text-navy mb-2">Select Your Record:</div>
              <ul className="space-y-2">
                {searchResult.map((student: any) => (
                  <li key={student.id} className="flex items-center justify-between border-b border-navy/10 pb-2">
                    <div>
                      <div><b>Name:</b> {student.student_name}</div>
                      <div><b>Email:</b> {student.email}</div>
                    </div>
                    <button type="button" className={buttonClass + ' w-auto ml-4 px-4 py-2 text-sm'} onClick={() => handleUseRecord(student)}>Use This Record</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(searchResult) && searchResult.length === 0 && (
            <div className="mt-4 text-navy">No students found for this email.</div>
          )}
        </form>
      )}
      {showMembershipOptions && !showMembershipTypeSelect && (
        <div className="mt-8">
          <h1 className="text-4xl font-extrabold text-navy mb-3 tracking-tight">Join Summit Martial Arts</h1>
          <p className="text-lg text-navy mb-8">Start your journey with a trial or full membership.</p>
          <div className="flex flex-col gap-6">
            <button className={buttonClass} onClick={() => navigate('/signup?type=trial')}>
              2-Week Trial Signup
              <div className="text-sm text-navy/80 mt-1 font-normal">Try unlimited classes for 14 days. $75, no commitment. Credit applies to membership if you join.</div>
            </button>
            <button className={buttonClass} onClick={() => setShowMembershipTypeSelect(true)}>
              Membership Signup
              <div className="text-sm text-navy/80 mt-1 font-normal">Choose a flexible month-to-month or discounted 12-month plan. Includes full member benefits.</div>
            </button>
          </div>
          <button className="mt-8 underline text-navy/70 text-sm" onClick={() => setShowMembershipOptions(false)}>Back</button>
        </div>
      )}
      {showMembershipTypeSelect && (
        <div className="mt-8">
          <h1 className="text-3xl font-bold text-navy mb-6">Choose Your Membership</h1>
          <div className="flex flex-col gap-6">
            <button className={buttonClass} onClick={() => { setMembershipType('month_to_month'); navigate('/signup'); }}>
              Month-to-Month Membership
              <div className="text-sm text-navy/80 mt-1 font-normal">$199/month. Cancel anytime. Full member benefits.</div>
            </button>
            <button className={buttonClass} onClick={() => { setMembershipType('yearly'); navigate('/signup'); }}>
              12-Month Membership (Save 20%)
              <div className="text-sm text-navy/80 mt-1 font-normal">$1900/year. Best value. Full member benefits.</div>
            </button>
          </div>
          <button className="mt-8 underline text-navy/70 text-sm" onClick={() => setShowMembershipTypeSelect(false)}>Back</button>
        </div>
      )}
    </div>
  );
};

const SignupForm = () => {
  const navigate = useNavigate();
  const { membershipType, setMemberId, setSignupForm } = useSignup();
  const [form, setForm] = useState({
    student_name: '',
    student_date_of_birth: '',
    parent_guardian_name: '',
    email: '',
    contact_phone_number: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAge = (dob: string) => {
    if (!dob) return null;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };
  const age = getAge(form.student_date_of_birth);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };
  const handleSubmit = async () => {
    setLoading(true); setError(null);
    try {
      // Map form fields to API fields
      const [first, ...lastArr] = (form.student_name || '').split(' ');
      const payloadForApi = {
        student_first_name: first || '',
        student_last_name: lastArr.join(' ') || '',
        parent_first_name: age !== null && age < 18 ? (form.parent_guardian_name?.split(' ')[0] || '') : undefined,
        parent_last_name: age !== null && age < 18 ? (form.parent_guardian_name?.split(' ').slice(1).join(' ') || '') : undefined,
        contact_emails: [form.email],
        contact_phone: form.contact_phone_number,
        source: 'membership-frontend',
        address_line1: '',
        student_birthdate: form.student_date_of_birth,
        membership_type: membershipType,
      };
      const res = await createMember(payloadForApi);
      setMemberId(res.id);
      setSignupForm(payloadForApi);
      navigate('/waiver');
    } catch (e: any) {
      if (e.message && e.message.toLowerCase().includes('already exists')) {
        setError('Student information is already in the system. Contact the Summit Martial Arts team to restart membership.');
      } else {
        setError(e.message || 'Failed to create member');
      }
    } finally {
      setLoading(false);
    }
  };
  const isTrial = membershipType === 'trial';
  return (
    <div className="max-w-xl mx-auto mt-12 bg-white rounded-2xl shadow-xl p-8 border border-gold">
      <h2 className="text-3xl font-bold text-navy mb-6">{isTrial ? 'Trial Sign Up' : 'Membership Sign Up'}</h2>
      {error && <div className="text-red-600 mb-2 text-sm font-medium">{error}</div>}
      <div className="space-y-4">
        <input name="student_name" placeholder="Student Name" value={form.student_name} onChange={handleChange} className={inputClass} />
        <input name="student_date_of_birth" type="date" placeholder="Date of Birth" value={form.student_date_of_birth} onChange={handleChange} className={inputClass} />
        {age !== null && age < 18 && (
          <input name="parent_guardian_name" placeholder="Parent/Guardian Name (if under 18)" value={form.parent_guardian_name} onChange={handleChange} className={inputClass} />
        )}
        <input name="email" placeholder="Email" value={form.email} onChange={handleChange} className={inputClass} />
        <input name="contact_phone_number" placeholder="Phone Number" value={form.contact_phone_number} onChange={handleChange} className={inputClass} />
      </div>
      <button className={buttonClass + " mt-8"} onClick={handleSubmit} disabled={loading}>{loading ? <span className="animate-pulse">Submitting...</span> : (isTrial ? 'Continue to Waiver' : 'Continue to Waiver')}</button>
    </div>
  );
};

const STRIPE_LINKS: Record<string, string> = {
  trial: 'https://buy.stripe.com/bJe3cudml1trgrc4Kv6AM00',
  month_to_month: 'https://buy.stripe.com/6oU5kCaa9egd3Eq2Cn6AM02',
  yearly: 'https://buy.stripe.com/bJebJ0fut7RP0se1yj6AM01',
};

const WaiverPage = () => {
  const navigate = useNavigate();
  const { memberId, signupForm } = useSignup();
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waiverHtml, setWaiverHtml] = useState('');

  React.useEffect(() => {
    if (!memberId) {
      setError('Session expired or invalid. Please start the signup process again.');
      setTimeout(() => navigate('/'), 2500);
    }
  }, [memberId, navigate]);

  const membershipType = signupForm?.membership_type || 'month_to_month';
  let waiverFile = '/waivers/waiver-membership-month.html';
  if (membershipType === 'yearly') waiverFile = '/waivers/waiver-membership-yearly.html';
  if (membershipType === 'trial') waiverFile = '/waivers/waiver-trial.html';

  React.useEffect(() => {
    fetch(waiverFile)
      .then(res => res.text())
      .then(setWaiverHtml);
  }, [waiverFile]);

  const handleSubmit = async () => {
    setLoading(true); setError(null);
    try {
      await submitWaiver(memberId, { waiver_text: waiverHtml, signature });
      if (signupForm?.email) {
        sessionStorage.setItem('profileEmail', signupForm.email);
      }
      window.location.href = STRIPE_LINKS[signupForm?.membership_type] || STRIPE_LINKS['month_to_month'];
    } catch (e: any) {
      setError(e.message || 'Failed to submit waiver');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="max-w-4xl w-full mx-auto mt-12 bg-white rounded-2xl shadow-xl p-0 border border-gold">
      <h2 className="text-3xl font-bold text-navy mb-6 px-8 pt-8">Waiver & Release</h2>
      {error && <div className="text-red-600 mb-2 text-sm font-medium px-8">{error}</div>}
      <div className="bg-navy text-white rounded-lg mb-6 text-left text-base leading-relaxed max-h-[70vh] overflow-y-auto px-0 sm:px-4 py-4">
        <div style={{ minWidth: 0 }} dangerouslySetInnerHTML={{ __html: waiverHtml }} />
      </div>
      <div className="px-8 pb-8">
        <input placeholder="Type your full name as signature" value={signature} onChange={e => setSignature(e.target.value)} className={inputClass} />
        <button className={buttonClass + " mt-8"} onClick={handleSubmit} disabled={loading || !memberId}>{loading ? <span className="animate-pulse">Submitting...</span> : 'Continue to Payment'}</button>
      </div>
    </div>
  );
};

const PaymentRedirect = () => {
  const { memberId } = useSignup();
  React.useEffect(() => {
    async function go() {
      try {
        const res = await createStripeSession(memberId);
        window.location.href = res.url;
      } catch {
        window.location.href = '/confirmation';
      }
    }
    go();
  }, [memberId]);
  return (
    <div className="max-w-xl mx-auto mt-24 text-center">
      <h2 className="text-3xl font-bold text-navy mb-4">Redirecting to Payment...</h2>
      <p className="text-lg text-navy">Please wait while we redirect you to Stripe for secure payment.</p>
    </div>
  );
};

const ConfirmationPage = () => (
  <div className="max-w-xl mx-auto mt-24 text-center bg-white rounded-2xl shadow-xl p-8 border border-gold">
    <h2 className="text-3xl font-bold text-navy mb-4">Thank You!</h2>
    <p className="text-lg text-navy">Your membership signup is complete. Check your email for confirmation and next steps.</p>
  </div>
);

const RegisterPage: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100">
    <RegisterMemberForm />
  </div>
);

function App() {
  const [membershipType, setMembershipType] = useState<string>('');
  const [memberId, setMemberId] = useState<string>('');
  const [signupForm, setSignupForm] = useState<any>(null);
  const contextValue = { membershipType, setMembershipType, memberId, setMemberId, signupForm, setSignupForm };
  const [showProfile, setShowProfile] = useState(false);
  const [profileEmail, setProfileEmail] = useState<string | null>(null);
  React.useEffect(() => {
    const email = sessionStorage.getItem('profileEmail');
    if (email) {
      setProfileEmail(email);
      setShowProfile(true);
      sessionStorage.removeItem('profileEmail');
    }
  }, []);
  return (
    <SignupContext.Provider value={contextValue}>
      <div className="min-h-screen bg-gradient-to-b from-navy to-white flex flex-col justify-center py-8 px-2">
        <Router>
          <Routes>
            {showProfile && profileEmail ? (
              <Route path="*" element={<ProfilePage email={profileEmail} onCompleteStep={async (student, step) => {
                setMemberId(student.id);
                setSignupForm(student);
                if (step === 'waiver') {
                  window.location.href = '/waiver';
                }
                if (step === 'payment') {
                  window.location.href = STRIPE_LINKS[student.membership_type] || STRIPE_LINKS['month_to_month'];
                }
              }} onBack={() => { setShowProfile(false); setProfileEmail(null); }} />} />
            ) : (
              <>
                <Route path="/" element={<LandingPage />} />
                <Route path="/signup" element={<SignupForm />} />
                <Route path="/waiver" element={<WaiverPage />} />
                <Route path="/payment" element={<PaymentRedirect />} />
                <Route path="/confirmation" element={<ConfirmationPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            )}
          </Routes>
        </Router>
      </div>
    </SignupContext.Provider>
  );
}

export default App;
